import { useState, useRef, useEffect } from 'react';
import cx from 'classnames';
import { Link } from '@plone/components';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import config from '@plone/registry';
import styles from '@redturtle/qubicaamf-theme/components/Blocks/ImageGallery/styles.module.css';
import { isInternalURL } from '@plone/volto/helpers/Url/Url';

export interface BaseLink {
  '@id': string;
  title?: string;
}

export interface Slide {
  '@id': string;
  description: string;
  image: any;
  title?: string;
  link: typeof Link | BaseLink; // TODO check if correct
  linkMore?: string;
}

interface SlidesProps {
  slides: Slide[];
  editMode: boolean;
  listing?: boolean;
  show_description?: boolean;
  show_title?: boolean;
}

const Slides = (props: SlidesProps) => {
  const {
    slides,
    editMode = false,
    listing = false,
    show_description = true,
    show_title = true,
  } = props;
  const Image = config.getComponent('Image').component;
  const [isScrolling, setIsScrolling] = useState(false);
  const [isScrollable, setIsScrollable] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Check if the container is scrollable
  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      const hasOverflow = container.scrollWidth > container.clientWidth;
      setIsScrollable(hasOverflow);
    }
  }, [slides]);

  const handleScroll = () => {
    // Show the scrollbar thumb
    setIsScrolling(true);

    // Clear any previous timeout
    if (scrollTimeout.current) {
      clearTimeout(scrollTimeout.current);
    }

    // Hide the scrollbar thumb after 1 second of no scrolling
    scrollTimeout.current = setTimeout(() => {
      setIsScrolling(false);
    }, 100); // Adjust the delay as needed
  };

  return (
    <div
      ref={containerRef}
      className={cx('block-imagegallery-slides', styles.slides, {
        [styles.scrollbar]: isScrolling,
        [styles.justifyCenter]: !isScrollable,
      })}
      onScroll={handleScroll}
    >
      {slides.map((slide) => {
        const linkIsExternal =
          Array.isArray(slide.link) &&
          !isInternalURL(slide?.link?.[0]?.['@id']);
        return (
          <div className={styles.slide} key={slide['@id']}>
            {slide.link ? (
              slide.image?.image_scales ? (
                <UniversalLink
                  item={linkIsExternal ? null : slide.link}
                  href={
                    linkIsExternal
                      ? slide.link?.[0]?.['@id']
                      : slide.link['@id']
                  }
                  title={slide.title || 'link'}
                  openLinkInNewTab={true}
                  className={styles.slideLink}
                  onClick={(e: React.SyntheticEvent<HTMLLinkElement>) => {
                    if (editMode) e.preventDefault();
                  }}
                >
                  {slide.linkMore && (
                    <>
                      <div className={styles.slideOverlayBackground}></div>
                      <div className={styles.slideOverlayText}>
                        {slide.linkMore}
                      </div>
                    </>
                  )}
                  <Image
                    alt={slide.title ?? ''}
                    item={slide.image}
                    className={cx(styles.slideImg, {
                      [styles.listingImg]: listing,
                    })}
                    sizes="(min-width: 576px) 540px, (min-width: 768px) 668px, (min-width: 992px) 960px, (min-width: 1200px) 1184px, (min-width: 1400px) 1320px"
                  />
                </UniversalLink>
              ) : (
                <UniversalLink
                  item={slide.link}
                  href={slide.link['@id']}
                  title={slide.title || 'link'}
                  openLinkInNewTab={true}
                  className={styles.slideLink}
                  onClick={(e: React.SyntheticEvent<HTMLLinkElement>) => {
                    if (editMode) e.preventDefault();
                  }}
                >
                  <div className={styles.noImg}>{slide.title}</div>
                </UniversalLink>
              )
            ) : (
              <Image
                alt={slide.title ?? ''}
                item={slide.image}
                className={cx(styles.slideImg, {
                  [styles.listingImg]: listing,
                })}
                sizes="(min-width: 576px) 540px, (min-width: 768px) 668px, (min-width: 992px) 960px, (min-width: 1200px) 1184px, (min-width: 1400px) 1320px"
              />
            )}
            {slide.title && show_title && (
              <div className={styles.slideImage}>
                <h3 className={styles.slideTitle}>{slide.title}</h3>
              </div>
            )}
            {slide.description && show_description && (
              <div className={styles.slidesDescription}>
                {slide.description}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default Slides;
